import { useEffect } from "react";
import { Router } from "@reach/router";
import { connect, useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton";
import { AppMenu } from "newui/features/AppMenu";
import {
	ExecutiveViewBanner,
	useExecutiveStaleStateReconcile,
} from "newui/features/ExecutiveView";
import {
	getDocuments,
	getPastProjects,
	getResources,
} from "actions/companyActions";

//Routes
import { MyAccount } from "../routes/account/MyAccount";
import { Breadcrumbs } from "components/Breadcrumb";
import Projects from "pages/RFQ/Projects/Projects";

import { SupplierListsPage as SupplierLists } from "newui/features/Rfx/SupplierList";
import { ProjectForm } from "pages/RFQ/Projects/ProjectForm";
import ProjectDetail from "pages/RFQ/Projects/ProjectDetail/ProjectDetail";
import SupplierForm from "pages/RFQ/SupplierList/SupplierForm";
import { SupplierDetail } from "pages/RFQ/SupplierList/SupplierDetail";
import { RFQ } from "pages/RFQ/RFQ";
import CreateRFQPage from "newui/features/Rfx/Creation/CreateRFQPage";
import ProcurerRFx from "pages/RFQ/RFQ/RFQDetail/ProcurerRFx";
import { Search } from "routes/search/Search";
import {
	Settings,
	RfxSettings,
	PdfSettingsPage,
} from "newui/features/Settings";
import { AddProject } from "components/ProjectForm/AddProject";
import { EditProject } from "components/ProjectForm/EditProject";
import { DocumentRequest } from "components/DocumentRequest";
import { UpdateCreditCard } from "components/CreditCardDetail";
import ViewRFQPage from "pages/RFQ/RFQ/ViewRFQ/ViewRFQPage";
import { Invoice } from "components/Invoice";
import {
	CurrentAccountType,
	useProfileContext,
} from "newui/features/Auth/context/ProfileContext";
import { CompanyListing } from "./CompanyListing";
import { EditUserAccount } from "components/EditUserAccount";
import { ManageTemplates } from "pages/RFQ/Templates/ManageTemplates";
import { ProtectedTemplateRoute } from "pages/RFQ/Templates/ProtectedTemplateRoute";
import { ProcurerDashboard } from "features/dashboard-procurer";
import { Box } from "@chakra-ui/react";
import { ContentContainer } from "components/Layout";
import { useCurrentUserHasDashboard } from "hooks/featureFlags";
import RFQPreview from "newui/features/Rfx/Submission/RFQPreview";
import { RfxDataProvider } from "newui/features/Rfx/data-provider/RfxDataProvider";
import { RfxSubmissionDataProvider } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";
import CreateRfxTemplate from "newui/features/Rfx/Creation/CreateRFxTemplate";
import GetRfxSubmissionByCompany from "app/GetRfxSubmissionByCompany";
import { EvaluationPage } from "newui/features/Rfx/Evaluation";
import { useCurrentCompany, useCurrentUser } from "hooks/auth";
import { CompanyProfile } from "newui/features/Company";
import { KeyPersonnelFormPage } from "newui/features/Company/KeyPersonnel";
import { selectAuthLoading } from "newui/features/Auth/authSelectors";
import { NotFound } from "newui/features/ErrorPages/NotFound";
import { ConfirmInsuranceDocumentPage } from "newui/features/Insurance/pages/ConfirmInsuranceDocumentPage";
import { MySuppliersPage } from "newui/features/MySuppliers/pages/MySuppliersPage";
import { SupplierDetailPage } from "newui/features/MySuppliers/pages/SupplierDetailPage";
import { NewRatingPage } from "newui/features/PerformanceRating/pages/NewRatingPage";
import { useHasPerformanceManagementAccess } from "hooks/featureFlags";
import { EditUserProfile } from "components/EditUserProfile";
import { UserListPage } from "newui/features/Auth/components/User/UserListPage";

export const AccountComponent = ({
	getPastProjects,
	getResources,
	getDocuments,
	path, // eslint-disable-line no-unused-vars
}) => {
	const profileContext = useProfileContext();
	const isLoading = useSelector(selectAuthLoading);
	const currentCompany = useCurrentCompany();
	const { currentUser } = useCurrentUser();
	const companyId = currentCompany?.id;
	const hasDashboardAccess = useCurrentUserHasDashboard();
	const hasPerformanceManagement = useHasPerformanceManagementAccess();

	// Mount stale-state reconciler once at the app shell level (PRO-506)
	useExecutiveStaleStateReconcile();

	useEffect(() => {
		//Set this company ID from the route variable - this allows anyone to access/edit all companies

		if (companyId) {
			getPastProjects(companyId);
			getResources(companyId);
			getDocuments(companyId);
		}
	}, [
		currentUser,
		currentCompany,
		getPastProjects,
		getDocuments,
		getResources,
		companyId,
	]);

	if (isLoading) {
		return (
			<div className="flex-grow p-[50px]">
				<Skeleton
					duration={0.5}
					count={10}
					width="100%"
				/>
			</div>
		);
	}

	return (
		<Box
			display="flex"
			flexWrap="nowrap"
			width="100%"
			minH="100vh"
		>
			<AppMenu />
			<Box
				display="flex"
				flexDirection="column"
				flex="1"
				minH="0"
			>
				<ExecutiveViewBanner />
				<ContentContainer
					id="element-scroll"
					noPadding={
						window?.location?.href.endsWith("/search") ||
						window?.location?.href.includes("/search/")
					}
				>
					<Breadcrumbs
						ignoreCompany={false}
						templateRoutes={{
							new: "Create RFx Template",
							edit: "Edit RFx Template",
							"manage-templates": "Manage Templates",
						}}
						parentPath={{
							"rfx-template": "manage-templates",
						}}
					/>

					<Router primary={false}>
						<MyAccount path="my-account" />

						{/* START OF ACCOUNT SETTING MENU */}
						<Settings
							path="account-settings"
							company={currentCompany}
						/>
						<EditUserAccount path="edit-user-account" />
						<Invoice
							path="account-settings/invoice"
							company={currentCompany}
						/>
						<UserListPage path="account-settings/users" />
						<UpdateCreditCard path="account-settings/update-card-details" />
						<RfxSettings
							path="rfx-settings/*"
							company={currentCompany}
						/>
						{profileContext?.currentProfile === CurrentAccountType.PROCURER && (
							<PdfSettingsPage path="pdf-settings" />
						)}
						{/* END OF ACCOUNT SETTING MENU */}

						{/* START OF COMPANIES LISTING */}
						<CompanyListing path="companies" />
						<CompanyListing
							path="accounts/*"
							isAdminMode
						/>
						{/* END OF COMPANIES LISTING */}

						{/* START OF COMPANY PROFILE MENU */}
						<CompanyProfile
							path="company-profile/:activeTab"
							company={currentCompany}
						/>

						<ConfirmInsuranceDocumentPage path="company-profile/documents/:documentId/confirm-verification" />
						<KeyPersonnelFormPage path="company-profile/:activeTab/add" />
						<KeyPersonnelFormPage path="company-profile/:activeTab/:id/edit" />

						<AddProject path="company-profile/:activeTab/create-project" />
						<EditProject
							path="company-profile/:activeTab/edit-project/:projectId"
							profileTypeId={currentCompany?.profile_type?.id}
						/>

						{/* END OF COMPANY PROFILE MENU */}

						<EditUserProfile
							path="edit-user-profile"
							company={"My Profile"}
						/>

						{/* RFx (RFx and RFQ are same) */}
						{profileContext?.isRfqFeatureEnabled && (
							<>
								{hasDashboardAccess ? (
									<ProcurerDashboard path="/" />
								) : (
									<Projects path="/" />
								)}
								{hasDashboardAccess && <ProcurerDashboard path="dashboard/*" />}

								<Projects path="projects" />
								<ProjectForm
									path="projects/new-project"
									newProject
								/>
								<ProjectForm
									path="projects/new-project/suppliers-list/:supplierListId"
									newProjectFromSupplierList
								/>
								<ProjectForm
									path="projects/edit-project/:projectId"
									editProject
								/>
								<ProjectDetail path="projects/:id/:slug" />

								<SupplierDetail path="supplier-list/:id/:slug" />
								<SupplierLists path="supplier-list" />
								<SupplierForm
									path="supplier-list/new-supplier-list"
									isNew
								/>
								<SupplierForm
									path="supplier-list/edit-supplier-list/:listId"
									isEdit
								/>

								<RFQ path="rfx" />

								<GetRfxSubmissionByCompany
									companyId={companyId}
									path="rfx/:rfxId/getsubmission/:submissionCompanyId/:currentTab"
								/>
								<RfxSubmissionDataProvider
									path="rfx-template"
									useParentRfxData={false}
								>
									<CreateRfxTemplate
										path="new"
										templateId={({ templateId }) => templateId}
									/>
									<ProtectedTemplateRoute
										path=":templateId"
										isEdit
										isTemplate
										templateId={({ templateId }) => templateId}
									/>
								</RfxSubmissionDataProvider>
								<RfxDataProvider path="rfx/:rfxRouteId">
									{/* Client Viewing submissions */}
									<RfxSubmissionDataProvider path="submission/:rfxInviteRouteId/:currentTab">
										<RFQPreview path="/" />
										<RFQPreview path=":contextId" />
									</RfxSubmissionDataProvider>

									{/* Client viewing sent RFx's */}
									<ProcurerRFx path=":slug/:currentTab/:contextId" />
									<ProcurerRFx path=":slug/:currentTab" />
									<ProcurerRFx path="/" />

									{/* RFx Evaluation */}
									<EvaluationPage path="evaluation/:evaluationId" />

									{/* Client Creating RFx and RFx templates */}
									<RfxSubmissionDataProvider path="/">
										<CreateRFQPage path="create-rfx" />
										<CreateRFQPage
											path="edit-rfx"
											isEdit
										/>
										<ViewRFQPage path="view" />
									</RfxSubmissionDataProvider>
								</RfxDataProvider>
								<ManageTemplates path="manage-templates" />
							</>
						)}
						{/* END OF RFx */}

						{/* Supplier Hub & Performance Rating */}
						{hasPerformanceManagement && (
							<>
								<MySuppliersPage path="my-suppliers" />
								<SupplierDetailPage path="my-suppliers/:supplierId" />
								<NewRatingPage path="my-suppliers/rate" />
								<NewRatingPage path="my-suppliers/rate/:supplierId" />
							</>
						)}

						<Search
							path="search/*"
							title="Search"
						/>
						<DocumentRequest path="company-profile/:activeTab/request-view" />
						<NotFound default />
					</Router>
				</ContentContainer>
			</Box>
		</Box>
	);
};

export const Account = connect(null, {
	getDocuments,
	getPastProjects,
	getResources,
})(AccountComponent);
